// MoreSectionsButton — the "More" control that toggles the contract tab bar's
// overflow row. Styled consistently with TabButton (text-body-sm font-medium),
// shows a chevron that flips with the open state, and carries an attention dot
// when any HIDDEN tab needs attention. The dot is purely visual (aria-hidden);
// the count of hidden tabs and the attention state are conveyed through the
// accessible name + title so hover and screen readers both explain it.

import { forwardRef } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { clsx } from "clsx";

interface MoreSectionsButtonProps {
  hiddenCount: number;
  open: boolean;
  hasAttention: boolean;
  onClick: () => void;
  /** Measurement-row marker so tests can key a stubbed width off it; harmless
   *  in production where the live button width is read via ref. */
  "data-measure"?: string;
}

const MoreSectionsButton = forwardRef<HTMLButtonElement, MoreSectionsButtonProps>(
  ({ hiddenCount, open, hasAttention, onClick, ...rest }, ref) => {
    const Chevron = open ? ChevronUp : ChevronDown;
    const label = `More sections, ${hiddenCount} hidden${
      hasAttention ? ", contains attention items" : ""
    }`;
    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls="contract-tabs-overflow-row"
        aria-label={label}
        title={label}
        {...rest}
        className={clsx(
          "relative flex items-center gap-1 py-3 px-1 border-b-2 border-transparent text-body-sm font-medium transition-colors",
          "text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300",
          "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 rounded-sm",
        )}
      >
        More
        <Chevron className="h-4 w-4" aria-hidden="true" />
        {hasAttention && (
          <span
            aria-hidden="true"
            className="absolute right-0 top-2 block h-[7px] w-[7px] rounded-full bg-red-500 ring-2 ring-slate-50 dark:bg-red-600 dark:ring-slate-900"
          />
        )}
      </button>
    );
  },
);

MoreSectionsButton.displayName = "MoreSectionsButton";

export default MoreSectionsButton;
